import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Instagram, Menu, Phone, X } from "lucide-react";
import { COMPANY, INSTAGRAM_URL } from "../../lib/content";
import { NAV } from "../../lib/content";
import { Logo } from "./Logo";

const NavA = ({ href, home, ...rest }) => (home ? <a href={href} {...rest} /> : <Link to={`/${href}`} {...rest} />);

export const Header = () => {
  const home = useLocation().pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || open;

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-300 ${
        solid ? "border-cs-navy/10 bg-white/85 shadow-[0_8px_30px_-12px_rgba(18,48,63,0.25)] backdrop-blur-xl" : "border-white/10 bg-cs-navy/40 backdrop-blur-md"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-8 md:h-20">
        <Logo light={!solid} testId="header-logo" className="shrink-0" href={home ? "#top" : "/"} />
        <nav className="ml-8 hidden items-center gap-6 whitespace-nowrap xl:flex 2xl:gap-8" aria-label="Navigation principale">
          {NAV.map((n) => (
            <NavA
              key={n.href}
              home={home}
              href={n.href}
              data-testid={`nav-link-${n.href.slice(1)}`}
              className={`nav-link text-[14px] font-semibold 2xl:text-[15px] ${solid ? "text-cs-navy" : "text-white"}`}
            >
              {n.label}
            </NavA>
          ))}
          <Link to="/app" data-testid="nav-employee-space" className={`nav-link text-[14px] font-semibold 2xl:text-[15px] ${solid ? "text-cs-navy" : "text-white"}`}>
            Espace employé
          </Link>
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" aria-label="Instagram @stopcheni.ch" data-testid="header-instagram-link"
            className={`grid h-9 w-9 place-items-center rounded-full transition-colors ${solid ? "text-cs-navy hover:bg-cs-navy/5" : "text-white hover:bg-white/10"}`}>
            <Instagram className="h-5 w-5" />
          </a>
          <a href={`tel:${COMPANY.tel}`} data-testid="header-phone-link" aria-label={`Appeler le ${COMPANY.phone}`}
            className={`hidden items-center gap-1.5 whitespace-nowrap text-[14px] font-semibold 2xl:inline-flex ${solid ? "text-cs-navy" : "text-white"}`}>
            <Phone className="h-4 w-4" /> {COMPANY.phone}
          </a>
          <NavA home={home} href="#devis" data-testid="header-cta-button" className="btn-accent whitespace-nowrap px-5 py-2.5 text-sm">
            Devis gratuit en 48 h
          </NavA>
        </nav>
        <button
          type="button"
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((o) => !o)}
          className={`grid h-10 w-10 place-items-center rounded-md xl:hidden ${solid ? "text-cs-navy" : "text-white"}`}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {open && (
        <nav data-testid="mobile-menu" className="border-t border-cs-navy/10 bg-white px-5 pb-6 pt-2 xl:hidden">
          {NAV.map((n) => (
            <NavA
              key={n.href}
              home={home}
              href={n.href}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-link-${n.href.slice(1)}`}
              className="block border-b border-cs-navy/5 py-3.5 text-base font-semibold text-cs-navy"
            >
              {n.label}
            </NavA>
          ))}
          <Link to="/app" data-testid="mobile-nav-employee-space" className="block border-b border-cs-navy/5 py-3.5 text-base font-semibold text-cs-navy">
            Espace employé
          </Link>
          <NavA home={home} href="#devis" onClick={() => setOpen(false)} data-testid="mobile-cta-button" className="btn-accent mt-5 w-full py-3.5">
            Devis gratuit en 48 h
          </NavA>
          <a href={`tel:${COMPANY.tel}`} data-testid="mobile-phone-link" className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-cs-navy/15 py-3.5 font-semibold text-cs-navy">
            <Phone className="h-4 w-4" /> Appeler le {COMPANY.phone}
          </a>
        </nav>
      )}
    </header>
  );
};
